import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleAzureADCallback, debugSessionStorage, isAuthenticationCompleted } from '../utils/azureAuth';
import { User } from '../types';

interface AuthCallbackProps {
  onAuthSuccess?: (user: User) => void;
}

export const AuthCallback: React.FC<AuthCallbackProps> = ({ onAuthSuccess }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'processing' | 'success' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  useEffect(() => {
    const processCallback = async () => {
      // Check if authentication was already completed
      if (isAuthenticationCompleted()) {
        console.log('✅ Authentication already completed, redirecting to home...');
        setStatus('success');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
        return;
      }
      
      // Prevent double processing
      if (isProcessing) {
        console.log('⏳ Already processing callback, skipping...');
        return;
      }
      
      setIsProcessing(true);
      setStatus('processing'); // Move to processing state once we start
      
      try {
        console.log('🔄 Processing Azure AD callback...');
        console.log('🔍 Browser state:', {
          userAgent: navigator.userAgent,
          currentUrl: window.location.href,
          referrer: document.referrer,
          sessionStorageLength: sessionStorage.length,
          localStorageLength: localStorage.length,
          timestamp: new Date().toISOString()
        });
        
        const user = await handleAzureADCallback(searchParams);
        console.log('✅ handleAzureADCallback returned user:', user);
        setStatus('success');
        
        // Call the success callback if provided
        if (onAuthSuccess) {
          console.log('📞 Calling onAuthSuccess with user...');
          onAuthSuccess(user);
          console.log('✅ onAuthSuccess completed');
        } else {
          console.warn('⚠️ No onAuthSuccess callback provided');
        }
        
        // Redirect to main app after brief delay
        setTimeout(() => {
          console.log('🔄 Redirecting to home page...');
          navigate('/', { replace: true });
        }, 2000);
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        
        // Handle specific cases that are part of normal flow
        if (errorMessage.includes('Authentication already in progress') || 
            errorMessage.includes('Authentication already completed')) {
          console.log('ℹ️ Authentication already in progress or completed, continuing...');
          // This is normal behavior in React StrictMode or when navigating back
          // Just wait a moment and redirect to home
          setStatus('success');
          setTimeout(() => {
            console.log('🔄 Redirecting to home page (auth already in progress)...');
            navigate('/', { replace: true });
          }, 1500);
          return;
        }
        
        console.error('❌ Authentication callback failed:', err);
        console.error('❌ Error details:', {
          name: err instanceof Error ? err.name : 'Unknown',
          message: errorMessage,
          stack: err instanceof Error ? err.stack : undefined,
          currentUrl: window.location.href,
          searchParams: Object.fromEntries(searchParams.entries()),
          timestamp: new Date().toISOString()
        });
        setError(errorMessage);
        setStatus('error');
        
        // Redirect back to login after delay
        setTimeout(() => {
          console.log('🔄 Redirecting to login page due to error...');
          navigate('/login', { replace: true });
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    // Only process if we have the required parameters
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    
    console.log('🔍 Auth callback parameters:', { 
      hasCode: !!code, 
      hasError: !!error,
      code: code?.substring(0, 20) + '...',
      error 
    });
    
    // Debug session storage state before processing
    debugSessionStorage();
    
    if (code || error) {
      processCallback();
    } else {
      console.error('❌ Missing required authentication parameters');
      console.log('🔍 Available URL parameters:', Object.fromEntries(searchParams.entries()));
      debugSessionStorage();
      setError('Missing required authentication parameters');
      setStatus('error');
      
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    }
  }, [searchParams, navigate, onAuthSuccess]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%)'
    }}>
      <div className="card" style={{ maxWidth: '500px', width: '100%', margin: '20px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img 
            src="/InnovationDayLogo2026.png" 
            alt="Creditsafe Innovation Day" 
            style={{ height: '120px', maxWidth: '100%' }}
          />
        </div>
        
        <div style={{ textAlign: 'center' }}>
          {status === 'checking' && (
            <>
              <div className="loading-spinner"></div>
              <h2 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                Checking Authentication...
              </h2>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
                Please wait while we verify your authentication.
              </p>
            </>
          )}
          
          {status === 'processing' && (
            <>
              <div className="loading-spinner"></div>
              <h2 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                Processing Authentication...
              </h2>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
                Please wait while we complete your login.
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#d4edda',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <svg style={{ width: '30px', height: '30px', color: '#155724' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                Authentication Successful!
              </h2>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '0' }}>
                Redirecting you to the application...
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#f8d7da',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px auto'
              }}>
                <svg style={{ width: '30px', height: '30px', color: '#721c24' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h2 style={{ marginBottom: '15px', color: '#2c3e50' }}>
                Authentication Failed
              </h2>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                {error || 'An unexpected error occurred during authentication.'}
              </p>
              <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                Redirecting to login page...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
